export const MODULE_TYPES = {
  BHP_RETAINER_DOCUMENT: "bhp_retainer_document",
  DISCONTINUANCE_QUESTIONNAIRE: "discontinuance_questionnaire",
  EMBEDDED_QUESTIONNAIRE: "embedded_questionnaire",
  ESIGN_DOCUMENT: "esign_document",
  MARIANA2026_RETAINER: "mariana2026_retainer",
  MARIANA_ANNOUNCEMENT: "mariana_announcement",
};
const LEGACY_QUESTIONNAIRE_TYPES = new Set(["quantification_mini_q"]);
const isPlainObject = (value) => {
  return value != null && typeof value === "object" && !Array.isArray(value);
};
export const getModuleContext = (module) => {
  if (isPlainObject(module?.context)) return module.context;
  if (isPlainObject(module?.contextJson)) return module.contextJson;
  if (isPlainObject(module?.context_json)) return module.context_json;
  return {};
};
const normalizeModuleType = (value) => {
  if (value === MODULE_TYPES.EMBEDDED_QUESTIONNAIRE) {
    return MODULE_TYPES.EMBEDDED_QUESTIONNAIRE;
  }
  if (LEGACY_QUESTIONNAIRE_TYPES.has(value)) {
    return MODULE_TYPES.EMBEDDED_QUESTIONNAIRE;
  }
  if (value === MODULE_TYPES.BHP_RETAINER_DOCUMENT) {
    return MODULE_TYPES.BHP_RETAINER_DOCUMENT;
  }
  if (value === MODULE_TYPES.DISCONTINUANCE_QUESTIONNAIRE) {
    return MODULE_TYPES.DISCONTINUANCE_QUESTIONNAIRE;
  }
  if (value === MODULE_TYPES.MARIANA2026_RETAINER) {
    return MODULE_TYPES.MARIANA2026_RETAINER;
  }
  if (value === MODULE_TYPES.MARIANA_ANNOUNCEMENT) {
    return MODULE_TYPES.MARIANA_ANNOUNCEMENT;
  }
  if (value === MODULE_TYPES.ESIGN_DOCUMENT) {
    return MODULE_TYPES.ESIGN_DOCUMENT;
  }
  return null;
};
const normalizeEsignDocumentContext = (context) => {
  const banner = isPlainObject(context.banner) ? context.banner : {};
  const popup = isPlainObject(context.popup) ? context.popup : {};
  return {
    ...context,
    banner: {
      enabled: banner.enabled !== false,
      ...(banner.title ? { title: banner.title } : {}),
      ...(banner.message ? { message: banner.message } : {}),
      ...(banner.cta ? { cta: banner.cta } : {}),
    },
    popup: {
      enabled: popup.enabled === true,
      ...(popup.mode ? { mode: popup.mode } : {}),
    },
  };
};
const normalizeQuestionnaireContext = (module, context) => {
  const banner = isPlainObject(context.banner) ? context.banner : {};
  const popup = isPlainObject(context.popup) ? context.popup : {};
  const questionnaireCode =
    typeof context.questionnaireCode === "string" && context.questionnaireCode.trim()
      ? context.questionnaireCode.trim()
      : LEGACY_QUESTIONNAIRE_TYPES.has(module?.module)
        ? module.module
        : null;
  return {
    ...context,
    ...(questionnaireCode ? { questionnaireCode } : {}),
    banner: {
      enabled: banner.enabled !== false,
      ...(banner.title ? { title: banner.title } : {}),
      ...(banner.message ? { message: banner.message } : {}),
      ...(banner.cta ? { cta: banner.cta } : {}),
    },
    popup: {
      enabled: popup.enabled === true,
      ...(popup.mode ? { mode: popup.mode } : {}),
    },
  };
};
const resolveModuleInstanceKey = (module, type, context) => {
  if (module?.instanceKey) return module.instanceKey;
  if (type === MODULE_TYPES.EMBEDDED_QUESTIONNAIRE) {
    return context?.invitationId || module?.id || type || null;
  }
  if (type === MODULE_TYPES.MARIANA_ANNOUNCEMENT) {
    return context?.userId || module?.id || type || null;
  }
  if (type === MODULE_TYPES.ESIGN_DOCUMENT) {
    return context?.envelopeId || module?.id || type || null;
  }
  return context?.matterId || module?.id || type || null;
};
const normalizeContextForType = (module, type, rawContext) => {
  if (type === MODULE_TYPES.EMBEDDED_QUESTIONNAIRE) {
    return normalizeQuestionnaireContext(module, rawContext);
  }
  if (type === MODULE_TYPES.ESIGN_DOCUMENT) {
    return normalizeEsignDocumentContext(rawContext);
  }
  return rawContext;
};
export const normalizeModule = (module) => {
  if (!isPlainObject(module)) return null;
  const rawType = module.type || module.module;
  const type = normalizeModuleType(rawType);
  if (!type) return null;
  const rawContext = getModuleContext(module);
  const context = normalizeContextForType(module, type, rawContext);
  const instanceKey = resolveModuleInstanceKey(module, type, context);
  return {
    id: module.id || `${type}:${instanceKey || "unknown"}`,
    type,
    enabled: module.enabled !== false,
    instanceKey,
    context,
    ...(module.status ? { status: module.status } : {}),
  };
};
export const normalizeModulesResponse = (payload) => {
  if (!Array.isArray(payload)) return [];
  return payload.map(normalizeModule).filter(Boolean);
};
export const isEmbeddedQuestionnaireModule = (module) => {
  return module?.type === MODULE_TYPES.EMBEDDED_QUESTIONNAIRE;
};
export const getModuleInstanceKey = (module) => {
  const rawType = module?.type || module?.module;
  const type = normalizeModuleType(rawType);
  const context = getModuleContext(module);
  return resolveModuleInstanceKey(module, type, context);
};
export const isSurveyStatusClosed = (status) => {
  return (
    status?.questionnaireCompleted === true ||
    String(status?.invitationStatus || "").toUpperCase() === "CLOSED"
  );
};
export const isEsignDocumentModule = (module) => {
  return module?.type === MODULE_TYPES.ESIGN_DOCUMENT;
};
// Only AVAILABLE envelopes can still be signed; SIGNED, DECLINED, EXPIRED and NOT_FOUND close the module.
export const isEsignStatusClosed = (status) => {
  const value = String(status?.status || "").toUpperCase();
  return value !== "" && value !== "AVAILABLE";
};
export const isModuleClosed = (module) => {
  const context = getModuleContext(module);
  return (
    String(module?.status || "").toUpperCase() === "CLOSED" ||
    isSurveyStatusClosed(context?.surveyStatus) ||
    isEsignStatusClosed(context?.esignStatus)
  );
};
const parseAllowedOrigins = (value) =>
  String(value || "")
    .split(",")
    .map((origin) => origin.trim().replace(/\/+$/, ""))
    .filter(Boolean);
/**
 * Returns the eSign origin for a module link, or null when the link is not served from an origin
 * listed in REACT_APP_ESIGN_ALLOWED_ORIGINS. Fails closed: with no allowlist, no link is trusted,
 * because the portal hands this origin a signing grant.
 */
export const getTrustedEsignOrigin = (
  link,
  allowedOrigins = process.env.REACT_APP_ESIGN_ALLOWED_ORIGINS,
) => {
  if (typeof link !== "string" || !link.trim()) return null;
  let url;
  try {
    url = new URL(link);
  } catch {
    return null;
  }
  if (url.username || url.password) return null;
  return parseAllowedOrigins(allowedOrigins).includes(url.origin) ? url.origin : null;
};
const isSignableEsignModule = (module) => {
  if (!isEsignDocumentModule(module) || module?.enabled !== true || isModuleClosed(module)) {
    return false;
  }
  const context = getModuleContext(module);
  return Boolean(context?.envelopeId && context?.matterId && getTrustedEsignOrigin(context?.link));
};
export const buildEsignDocumentAlerts = (modules) => {
  if (!Array.isArray(modules)) return [];
  return modules
    .filter(isSignableEsignModule)
    .filter((module) => getModuleContext(module)?.banner?.enabled !== false)
    .map((module) => {
      const context = getModuleContext(module);
      const banner = isPlainObject(context.banner) ? context.banner : {};
      const documentTitle =
        typeof context.documentTitle === "string" ? context.documentTitle.trim() : "";
      return {
        id: `module_${module.id || getModuleInstanceKey(module)}`,
        title: banner.title || "Documento para assinar",
        message:
          banner.message ||
          (documentTitle
            ? `O documento "${documentTitle}" aguarda a sua assinatura.`
            : "Há um documento aguardando a sua assinatura."),
        cta: banner.cta || "Assinar agora",
        action: "portal-module",
        module,
      };
    });
};
export const selectAutoOpenEsignDocument = (modules) => {
  if (!Array.isArray(modules)) return null;
  return (
    modules.filter(isSignableEsignModule).find((module) => {
      const popup = getModuleContext(module)?.popup;
      if (!isPlainObject(popup) || popup.enabled !== true) return false;
      return String(popup.mode || "on_login").toLowerCase() === "on_login";
    }) || null
  );
};
export const mergeEsignStatusIntoModule = (module, status) => {
  if (!isEsignDocumentModule(module) || !isPlainObject(status)) {
    return module;
  }
  const context = getModuleContext(module);
  return {
    ...module,
    status: isEsignStatusClosed(status) ? "CLOSED" : module?.status,
    context: {
      ...context,
      esignStatus: status,
    },
  };
};
export const buildEmbeddedQuestionnaireAlerts = (modules) => {
  if (!Array.isArray(modules)) return [];
  return modules
    .filter(isEmbeddedQuestionnaireModule)
    .filter((module) => module?.enabled === true)
    .filter((module) => !isModuleClosed(module))
    .filter((module) => getModuleContext(module)?.banner?.enabled !== false)
    .map((module) => {
      const context = getModuleContext(module);
      const banner = isPlainObject(context.banner) ? context.banner : {};
      return {
        id: `module_${module.id || getModuleInstanceKey(module)}`,
        title: banner.title || "Novo questionário disponível",
        message:
          banner.message ||
          "Precisamos que você responda algumas perguntas adicionais.",
        cta: banner.cta || "Responder agora",
        action: "portal-module",
        module,
      };
    });
};
export const selectAutoOpenEmbeddedQuestionnaire = (modules) => {
  if (!Array.isArray(modules)) return null;
  return (
    modules
      .filter(isEmbeddedQuestionnaireModule)
      .filter((module) => module?.enabled === true)
      .filter((module) => !isModuleClosed(module))
      .find((module) => {
        const popup = getModuleContext(module)?.popup;
        if (!isPlainObject(popup) || popup.enabled !== true) {
          return false;
        }
        const mode = String(popup.mode || "on_login").toLowerCase();
        return mode === "on_login";
      }) || null
  );
};
export const mergeSurveyStatusIntoModule = (module, status) => {
  if (!isEmbeddedQuestionnaireModule(module)) {
    return module;
  }
  const context = getModuleContext(module);
  return {
    ...module,
    status: isSurveyStatusClosed(status)
      ? "CLOSED"
      : status?.invitationStatus || module?.status,
    context: {
      ...context,
      surveyStatus: status,
    },
  };
};
export const applyLatestEmbeddedQuestionnaireStatuses = (
  currentModules,
  nextModules,
) => {
  if (!Array.isArray(nextModules)) return [];
  if (!Array.isArray(currentModules) || currentModules.length === 0) {
    return nextModules;
  }

  const freshestClosedStatusesByInvitation = new Map();

  currentModules.forEach((module) => {
    if (!isEmbeddedQuestionnaireModule(module) || !isModuleClosed(module)) {
      return;
    }

    const invitationId = getModuleInstanceKey(module);
    const surveyStatus = getModuleContext(module)?.surveyStatus;

    if (!invitationId || !surveyStatus) {
      return;
    }

    freshestClosedStatusesByInvitation.set(invitationId, surveyStatus);
  });

  if (freshestClosedStatusesByInvitation.size === 0) {
    return nextModules;
  }

  return nextModules.map((module) => {
    if (!isEmbeddedQuestionnaireModule(module) || isModuleClosed(module)) {
      return module;
    }

    const invitationId = getModuleInstanceKey(module);
    const surveyStatus = freshestClosedStatusesByInvitation.get(invitationId);

    if (!surveyStatus) {
      return module;
    }

    return mergeSurveyStatusIntoModule(module, surveyStatus);
  });
};
export const applyLatestEsignStatuses = (currentModules, nextModules) => {
  if (!Array.isArray(nextModules)) return [];
  if (!Array.isArray(currentModules) || currentModules.length === 0) {
    return nextModules;
  }
  const knownStatusesByEnvelope = new Map();
  currentModules.forEach((module) => {
    const esignStatus = getModuleContext(module)?.esignStatus;
    const envelopeId = getModuleInstanceKey(module);
    if (isEsignDocumentModule(module) && envelopeId && isPlainObject(esignStatus)) {
      knownStatusesByEnvelope.set(envelopeId, esignStatus);
    }
  });
  if (knownStatusesByEnvelope.size === 0) {
    return nextModules;
  }
  return nextModules.map((module) => {
    if (!isEsignDocumentModule(module) || getModuleContext(module)?.esignStatus) {
      return module;
    }
    const esignStatus = knownStatusesByEnvelope.get(getModuleInstanceKey(module));
    return esignStatus ? mergeEsignStatusIntoModule(module, esignStatus) : module;
  });
};
export const isBhpRetainerDocumentModule = (module) => {
  return module?.type === MODULE_TYPES.BHP_RETAINER_DOCUMENT;
};
const getRetainerStatus = (module) => {
  const context = getModuleContext(module);
  return String(
    module?.status || context?.status || context?.retainerStatus || "",
  )
    .toLowerCase()
    .trim();
};
export const isRetainerSignedModule = (module) => {
  const status = getRetainerStatus(module);
  return status === "signed" || status === "completed" || status === "closed";
};
export const hasProjectRModuleForMatter = (modules, matterId) => {
  if (!Array.isArray(modules) || !matterId) return false;
  return modules.some((module) => {
    if (!isBhpRetainerDocumentModule(module) || module?.enabled !== true) {
      return false;
    }
    const contextMatterId = getModuleContext(module)?.matterId;
    return module?.instanceKey === matterId || contextMatterId === matterId;
  });
};
export const findBhpRetainerModuleForMatter = (modules, matterId) => {
  if (!Array.isArray(modules) || !matterId) return null;
  return modules.find((module) => {
    if (!isBhpRetainerDocumentModule(module) || module?.enabled !== true) {
      return false;
    }
    const contextMatterId = getModuleContext(module)?.matterId;
    return module?.instanceKey === matterId || contextMatterId === matterId;
  }) || null;
};
export const shouldShowProjectRFlowForMatter = (modules, matterId) => {
  const module = findBhpRetainerModuleForMatter(modules, matterId);
  if (!module) return false;
  return !isRetainerSignedModule(module);
};
export const isDiscontinuanceQuestionnaireModule = (module) => {
  return module?.type === MODULE_TYPES.DISCONTINUANCE_QUESTIONNAIRE;
};
export const isMariana2026RetainerModule = (module) => {
  return module?.type === MODULE_TYPES.MARIANA2026_RETAINER;
};
export const isMarianaAnnouncementModule = (module) => {
  return module?.type === MODULE_TYPES.MARIANA_ANNOUNCEMENT;
};
export const findActiveMarianaAnnouncementModule = (modules) => {
  if (!Array.isArray(modules)) return null;
  return (
    modules.find(
      (module) => isMarianaAnnouncementModule(module) && module?.enabled === true,
    ) || null
  );
};
export const findBhpRetainerModule = (modules) => {
  if (!Array.isArray(modules)) return null;
  return modules.find((m) => isBhpRetainerDocumentModule(m) && m?.enabled === true) || null;
};
export const findActiveMariana2026RetainerModule = (modules) => {
  if (!Array.isArray(modules)) return null;
  return modules.find(
    (module) => isMariana2026RetainerModule(module) && module?.enabled === true,
  ) || null;
};
export const findActiveMariana2026RetainerModuleForMatter = (modules, matterId) => {
  if (!Array.isArray(modules) || !matterId) return null;
  return (
    modules.find((module) => {
      if (!isMariana2026RetainerModule(module) || module?.enabled !== true) {
        return false;
      }
      const contextMatterId = getModuleContext(module)?.matterId;
      return module?.instanceKey === matterId || contextMatterId === matterId;
    }) || null
  );
};
export const findDiscontinuanceModule = (modules, matterId) => {
  if (!Array.isArray(modules)) return null;
  return modules.find(
    (m) =>
      isDiscontinuanceQuestionnaireModule(m) &&
      m?.enabled === true &&
      getModuleContext(m)?.matterId === matterId,
  ) || null;
};
export const findAllDiscontinuanceModules = (modules) => {
  if (!Array.isArray(modules)) return [];
  return modules.filter(
    (m) => isDiscontinuanceQuestionnaireModule(m) && m?.enabled === true,
  );
};
